/**
 * 策略对比图表组件
 */

import React, { useEffect, useRef, useState } from 'react';
import { Card, Alert, Select, Row, Col, Statistic, Switch } from 'antd';
import * as echarts from 'echarts';
import type { EChartsOption } from 'echarts';
import { ComparisonChartProps, ChartData } from './types';

const { Option } = Select;

const ComparisonChart: React.FC<ComparisonChartProps> = ({
  data,
  metric = 'return',
  loading = false,
  error,
  config,
  onSymbolToggle,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [visibleSymbols, setVisibleSymbols] = useState<Record<string, boolean>>({});

  // 获取所有股票代码
  const symbols = Object.keys(data);

  // 初始化可见状态
  useEffect(() => {
    const initialVisible: Record<string, boolean> = {};
    symbols.forEach(symbol => {
      initialVisible[symbol] = true;
    });
    setVisibleSymbols(initialVisible);
  }, [symbols.join(',')]);

  useEffect(() => {
    if (!chartRef.current || loading || error || symbols.length === 0) return;

    // 初始化图表
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    let option: EChartsOption = {};

    // 生成颜色数组
    const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];

    switch (metric) {
      case 'price':
        // 价格对比
        const priceSeries = symbols
          .filter(symbol => visibleSymbols[symbol])
          .map((symbol, index) => {
            const symbolData = data[symbol] || [];
            return {
              name: symbol,
              type: 'line' as const,
              data: symbolData.map(item => [item.timestamp, item.value]),
              smooth: true,
              lineStyle: { 
                color: colors[index % colors.length], 
                width: 2 
              },
              symbol: 'none'
            };
          });

        option = {
          backgroundColor: config?.theme?.backgroundColor || 'transparent',
          title: {
            text: '价格对比',
            left: 'center'
          },
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross'
            },
            formatter: (params: any) => {
              let result = `${params[0].axisValueLabel}<br/>`;
              params.forEach((param: any) => {
                const value = param.data[1];
                result += `${param.seriesName}: ${value.toFixed(2)}<br/>`;
              });
              return result;
            }
          },
          legend: {
            data: symbols.filter(symbol => visibleSymbols[symbol]),
            top: 30
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
          },
          xAxis: {
            type: 'time'
          },
          yAxis: {
            type: 'value'
          },
          series: priceSeries
        };
        break;

      case 'return':
        // 收益率对比
        const returnSeries = symbols
          .filter(symbol => visibleSymbols[symbol])
          .map((symbol, index) => {
            const symbolData = data[symbol] || [];
            // 计算累计收益率
            const returns = symbolData.map((item, idx) => {
              if (idx === 0) return [item.timestamp, 0];
              const prevValue = symbolData[0].value;
              const currentValue = item.value;
              const returnRate = ((currentValue - prevValue) / prevValue) * 100;
              return [item.timestamp, returnRate];
            });

            return {
              name: symbol,
              type: 'line' as const,
              data: returns,
              smooth: true,
              lineStyle: { 
                color: colors[index % colors.length], 
                width: 2 
              },
              symbol: 'none'
            };
          });

        option = {
          backgroundColor: config?.theme?.backgroundColor || 'transparent',
          title: {
            text: '收益率对比',
            left: 'center'
          },
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross'
            },
            formatter: (params: any) => {
              let result = `${params[0].axisValueLabel}<br/>`;
              params.forEach((param: any) => {
                const value = param.data[1];
                result += `${param.seriesName}: ${value.toFixed(2)}%<br/>`;
              });
              return result;
            }
          },
          legend: {
            data: symbols.filter(symbol => visibleSymbols[symbol]),
            top: 30
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
          },
          xAxis: {
            type: 'time'
          },
          yAxis: {
            type: 'value',
            axisLabel: {
              formatter: '{value}%'
            }
          },
          series: returnSeries
        };
        break;

      case 'volatility':
        // 波动率对比
        const volatilitySeries = symbols
          .filter(symbol => visibleSymbols[symbol])
          .map((symbol, index) => {
            const symbolData = data[symbol] || [];
            // 计算滚动波动率
            const volatilityData = symbolData.map((item, idx) => {
              if (idx < 20) return [item.timestamp, 0]; // 需要至少20个数据点
              
              const window = symbolData.slice(idx - 19, idx + 1);
              const returns = window.map((d, i) => {
                if (i === 0) return 0;
                return (d.value - window[i - 1].value) / window[i - 1].value;
              }).slice(1);
              
              const mean = returns.reduce((sum, r) => sum + r, 0) / returns.length;
              const variance = returns.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / returns.length;
              const volatility = Math.sqrt(variance) * Math.sqrt(252) * 100; // 年化波动率
              
              return [item.timestamp, volatility];
            });

            return {
              name: symbol,
              type: 'line' as const,
              data: volatilityData,
              smooth: true,
              lineStyle: { 
                color: colors[index % colors.length], 
                width: 2 
              },
              symbol: 'none'
            };
          });

        option = {
          backgroundColor: config?.theme?.backgroundColor || 'transparent',
          title: {
            text: '波动率对比',
            left: 'center'
          },
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross'
            },
            formatter: (params: any) => {
              let result = `${params[0].axisValueLabel}<br/>`;
              params.forEach((param: any) => {
                const value = param.data[1];
                result += `${param.seriesName}: ${value.toFixed(2)}%<br/>`;
              });
              return result;
            }
          },
          legend: {
            data: symbols.filter(symbol => visibleSymbols[symbol]),
            top: 30
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
          },
          xAxis: {
            type: 'time'
          },
          yAxis: {
            type: 'value',
            axisLabel: {
              formatter: '{value}%'
            }
          },
          series: volatilitySeries
        };
        break;

      case 'sharpe':
        // 夏普比率对比
        const sharpeData = symbols
          .filter(symbol => visibleSymbols[symbol])
          .map(symbol => {
            const symbolData = data[symbol] || [];
            if (symbolData.length < 2) return { name: symbol, value: 0 };
            
            // 计算收益率序列
            const returns = symbolData.slice(1).map((item, idx) => {
              const prevValue = symbolData[idx].value;
              return (item.value - prevValue) / prevValue;
            });
            
            // 计算平均收益率和标准差
            const avgReturn = returns.reduce((sum, r) => sum + r, 0) / returns.length;
            const variance = returns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / returns.length;
            const stdDev = Math.sqrt(variance);
            
            // 夏普比率 (假设无风险利率为0)
            const sharpeRatio = stdDev === 0 ? 0 : (avgReturn * 252) / (stdDev * Math.sqrt(252));
            
            return { name: symbol, value: sharpeRatio };
          });

        option = {
          backgroundColor: config?.theme?.backgroundColor || 'transparent',
          title: {
            text: '夏普比率对比',
            left: 'center'
          },
          tooltip: {
            trigger: 'item',
            formatter: '{a} <br/>{b}: {c}'
          },
          legend: {
            data: symbols.filter(symbol => visibleSymbols[symbol]),
            top: 30
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: sharpeData.map(item => item.name)
          },
          yAxis: {
            type: 'value',
            name: '夏普比率'
          },
          series: [
            {
              name: '夏普比率',
              type: 'bar' as const,
              data: sharpeData.map(item => item.value),
              itemStyle: {
                color: (params: any) => {
                  return colors[params.dataIndex % colors.length];
                }
              }
            }
          ]
        };
        break;

      default:
        option = {
          backgroundColor: config?.theme?.backgroundColor || 'transparent',
          title: {
            text: '对比分析',
            left: 'center'
          },
          series: []
        };
    }

    chartInstance.current.setOption(option);

    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [data, metric, visibleSymbols, config, loading, error]);

  // 切换股票可见性
  const handleSymbolToggle = (symbol: string, visible: boolean) => {
    setVisibleSymbols(prev => ({
      ...prev,
      [symbol]: visible
    }));
    
    if (onSymbolToggle) {
      onSymbolToggle(symbol, visible);
    }
  };

  // 计算统计指标
  const calculateMetrics = () => {
    const metrics: Record<string, any> = {};

    symbols.forEach(symbol => {
      const symbolData = data[symbol] || [];
      if (symbolData.length > 0) {
        const firstValue = symbolData[0].value;
        const lastValue = symbolData[symbolData.length - 1].value;
        const totalReturn = ((lastValue - firstValue) / firstValue) * 100;
        
        metrics[symbol] = {
          totalReturn,
          currentValue: lastValue,
          dataPoints: symbolData.length
        };
      }
    });

    return metrics;
  };

  const metrics = calculateMetrics();

  if (error) {
    return <Alert message="图表加载失败" description={error} type="error" showIcon />;
  }

  return (
    <Card 
      title="对比分析" 
      loading={loading}
      extra={
        <Select
          value={metric}
          style={{ width: 120 }}
          onChange={(value) => {
            console.log('切换对比指标:', value);
          }}
        >
          <Option value="price">价格</Option>
          <Option value="return">收益率</Option>
          <Option value="volatility">波动率</Option>
          <Option value="sharpe">夏普比率</Option>
        </Select>
      }
    >
      {/* 股票选择器 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {symbols.map(symbol => (
          <Col span={6} key={symbol}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Statistic
                title={symbol}
                value={metrics[symbol]?.totalReturn || 0}
                precision={2}
                suffix="%"
                valueStyle={{ 
                  color: (metrics[symbol]?.totalReturn || 0) >= 0 ? '#52c41a' : '#ff4d4f',
                  fontSize: '14px'
                }}
              />
              <Switch
                size="small"
                checked={visibleSymbols[symbol]}
                onChange={(checked) => handleSymbolToggle(symbol, checked)}
              />
            </div>
          </Col>
        ))}
      </Row>

      <div ref={chartRef} style={{ width: '100%', height: '400px' }} />
    </Card>
  );
};

export default ComparisonChart;